import { useState } from 'react';

import { Event } from 'store/api/event';
import { useSelectedCloverDevice } from 'hooks';
import { Select } from 'components/select/select';
import { findReader, ReaderStatus, isHeldByOther } from 'components/reader-status';
import { useLiveReaders } from 'components/select-clover-device/use-event-readers';
import { useReaderStatusListQuery } from 'store/api/clover/reader-status';

// Owner 24 Oct (part J): the reader pop-up never picks a reader for the rep.
// Its list is read again each time it opens, and only a reader the server
// reports Ready can be chosen (never the one that just refused, never one in
// another sale, offline, or with its Clover app closed). Mounted only while
// the pop-up is open.
const NOT_WORKING = ['offline', 'app_closed', 'login', 'unknown', 'checking'];

export const ReaderChoice = ({
  event,
  chosen,
  onChoose,
  refusedDeviceId,
}: {
  event: Event;
  chosen: number | null;
  refusedDeviceId?: number;
  onChoose: (deviceId: number) => void;
}) => {
  const { deviceOptions } = useSelectedCloverDevice(event);
  // Reader hold (owner 24 Oct, "keep it live"): the same live list as the
  // cart (push + 5 s read of the server's own data), read again when the
  // pop-up opens. Never choose from an answer older than the pop-up; after
  // that, updates arrive without switching the list off.
  // Part J (and review M2): the pop-up also asks the readers' own status once
  // when it opens (the list that checks Clover for an answer over a minute
  // old). A reader is offered only when the live list says Ready and nobody
  // else holds it, and that open-time answer did not find it off (offline,
  // app closed, login); a hold seen at opening may end while it is open.
  const [openedAt] = useState(() => Date.now());
  const live = useLiveReaders(event.id);
  const opened = useReaderStatusListQuery(
    { eventId: event.id },
    { refetchOnMountOrArgChange: true }
  );
  const openRead = opened.isFetching || opened.isError ? undefined : opened.data?.readers;
  const fresh = live.readAt != null && live.readAt >= openedAt && !live.stale;
  const readers = fresh && openRead ? live.readers : undefined;
  const canChoose = (deviceId?: string | number | null) => {
    const reader = findReader(readers, deviceId);
    return Number(deviceId) !== Number(refusedDeviceId) &&
      reader?.status === 'ready' && !isHeldByOther(reader) &&
      !!findReader(openRead, deviceId) &&
      !NOT_WORKING.includes(findReader(openRead, deviceId)!.status);
  };
  const anyReady = deviceOptions.some((option) => canChoose(option.value));

  const note = !readers && !live.failed && !opened.isError
    ? 'Checking the readers…'
    : !readers
    ? "Couldn't check the readers. Close, then choose a reader in the cart."
    : !anyReady
    ? 'No other reader is Ready right now. Take another payment method, or try again in a minute.'
    : null;

  return (
    <div>
      <Select
        label="Choose another reader"
        placeholder="Choose a reader"
        options={deviceOptions}
        isDisabled={!anyReady}
        value={
          deviceOptions.find((option) => Number(option.value) === chosen) ??
          null
        }
        isOptionDisabled={(option: { value?: string | number | null }) =>
          !canChoose(option.value)
        }
        formatOptionLabel={(option: { value?: string | number | null; label?: string }) => {
          const reader = findReader(readers, option.value);
          return reader ? (
            <ReaderStatus
              dotAfter
              reader={reader}
              nameOnly={option.label}
              testId={`reader-choice-${option.value}`}
              note={reader.status === 'ready' ? undefined : reader.word}
            />
          ) : (
            option.label
          );
        }}
        onChange={(value) => {
          const option = Array.isArray(value) ? undefined : (value as { value?: string | number | null } | null);
          if (option && canChoose(option.value)) {
            onChoose(Number(option.value));
          }
        }}
      />
      {note && (
        <p role="status" style={{ margin: '8px 0 0', lineHeight: 1.6 }}>
          {note}
        </p>
      )}
    </div>
  );
};
